import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import apiClient from "../../api-services/ApiClient";
import { API } from "../../api-services/ApiList";
import { SITELINKS } from "../../app-routes/links";
import {
  FieldErrors,
  resetPasswordSchema,
  resetPasswordType,
  toFieldErrors,
} from "../../interface/auth.interface";
import { getErrorMessage } from "../../utils";
import AuthLayout from "./component/AuthLayout";
import {
  AuthHeading,
  BackLink,
  CodeInput,
  FormAlert,
  PasswordField,
  SubmitButton,
  TextField,
  textLinkClass,
} from "./component/FormControls";

const RESEND_COOLDOWN = 60; // seconds

type NavState = { email?: string; notice?: string; justSent?: boolean } | null;

/**
 * Supports two ways in:
 *  1. Code flow: /forgot-password -> email gets a 6-digit code -> enter it here
 *  2. Link flow: email contains /reset-password?token=...&email=... (code field is hidden)
 * Either way the API receives { email, token, password, password_confirmation }.
 */
const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const navState = location.state as NavState;

  const linkToken = params.get("token") ?? "";
  const usingLink = !!linkToken;

  const [form, setForm] = useState({
    email: params.get("email") ?? navState?.email ?? "",
    password: "",
    password_confirmation: "",
  });
  const [code, setCode] = useState("");
  const [errors, setErrors] = useState<FieldErrors<resetPasswordType>>({});
  const [notice, setNotice] = useState(navState?.notice ?? "");
  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(navState?.justSent ? RESEND_COOLDOWN : 0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const key = name as keyof resetPasswordType;
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setServerError("");

    const payload = { ...form, token: usingLink ? linkToken : code };
    const result = resetPasswordSchema.safeParse(payload);
    const fieldErrors = result.success ? {} : toFieldErrors(result.error);

    if (!usingLink && !/^\d{6}$/.test(code)) {
      fieldErrors.token = "Enter the 6-digit code from your email";
    }

    if (!result.success || Object.keys(fieldErrors).length) {
      setErrors(fieldErrors);
      return;
    }

    setErrors({});
    setLoading(true);
    try {
      await apiClient.post(API.RESET_PASSWORD, result.data);
      navigate(SITELINKS.SIGNIN, {
        replace: true,
        state: { notice: "Password updated. Sign in with your new password." },
      });
    } catch (err) {
      setServerError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setServerError("");
    setNotice("");
    const email = form.email.trim();
    if (!email) {
      setErrors((prev) => ({ ...prev, email: "Enter your email address first" }));
      return;
    }

    setResending(true);
    try {
      await apiClient.post(API.FORGOT_PASSWORD, { email });
      setCode("");
      setNotice(`A new code is on its way to ${email}.`);
      setCooldown(RESEND_COOLDOWN);
    } catch (err) {
      setServerError(getErrorMessage(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthLayout title="Set a new password | HoneConnect Admin">
      <BackLink to={SITELINKS.SIGNIN} label="Back to sign in" />

      <AuthHeading
        title="Set a new password"
        description={
          usingLink
            ? "Choose a new password for your admin account."
            : "Enter the code we emailed you, then choose a new password."
        }
      />

      {!serverError && <FormAlert tone="success" message={notice} />}
      <FormAlert tone="error" message={serverError} />

      <form onSubmit={handleSubmit} noValidate className="space-y-5">
        <TextField
          id="email"
          name="email"
          type="email"
          label="Email address"
          placeholder="name@company.com"
          autoComplete="username"
          inputMode="email"
          value={form.email}
          onChange={handleChange}
          error={errors.email}
          readOnly={usingLink}
        />

        {!usingLink && (
          <div>
            <div className="mb-1.5 flex items-baseline justify-between gap-3">
              <span className="text-sm font-medium text-honeConnectBlack">
                Reset code
              </span>
              <button
                type="button"
                onClick={handleResend}
                disabled={resending || cooldown > 0}
                className={`text-sm ${textLinkClass}`}
              >
                {resending
                  ? "Sending"
                  : cooldown > 0
                  ? `Resend in ${cooldown}s`
                  : "Resend code"}
              </button>
            </div>
            <CodeInput
              value={code}
              autoFocus={!!form.email}
              onChange={(v) => {
                setCode(v);
                if (errors.token) setErrors((prev) => ({ ...prev, token: undefined }));
              }}
              error={errors.token}
            />
          </div>
        )}

        <PasswordField
          id="password"
          name="password"
          label="New password"
          placeholder="Create a new password"
          autoComplete="new-password"
          value={form.password}
          onChange={handleChange}
          error={errors.password}
          hint="At least 8 characters."
        />

        <PasswordField
          id="password_confirmation"
          name="password_confirmation"
          label="Confirm new password"
          placeholder="Re-enter the new password"
          autoComplete="new-password"
          value={form.password_confirmation}
          onChange={handleChange}
          error={errors.password_confirmation}
        />

        {usingLink && errors.token && (
          <FormAlert
            tone="error"
            message="This reset link is invalid. Request a new one from the sign-in page."
          />
        )}

        <div className="pt-3">
          <SubmitButton loading={loading} loadingText="Updating password">
            Update password
          </SubmitButton>
        </div>
      </form>
    </AuthLayout>
  );
};

export default ResetPassword;
